import { __rest } from "tslib";
import { formatUrl } from './utils/format-url';
import { performHttpAction } from './utils/perform-http-action';
export const defaultHttpClientConfig = {
    baseUrl: undefined,
    credentials: 'same-origin',
    headers: new Headers(),
    maxAttempts: 3,
    requestTimeoutInMilliseconds: 30000,
    parseResponse: true
};
/**
 * Creates a HTTP Client using the provided configuration.
 *
 * @param config The configuration to merge with default configuration. If `undefined` the default configuration will be used.
 */
export function createHttpClient(config) {
    const mergedConfig = Object.assign(Object.assign({}, defaultHttpClientConfig), config);
    if (mergedConfig.baseUrl) {
        try {
            // tslint:disable-next-line:no-unused-expression
            new URL('', mergedConfig.baseUrl);
        }
        catch (_a) {
            throw new Error(`Base URL '${mergedConfig.baseUrl}' is invalid`);
        }
    }
    return {
        get: async (url, requestConfig) => {
            const actionConfig = getActionConfig(url, 'GET', mergedConfig, requestConfig);
            return performHttpAction(actionConfig);
        },
        post: async (url, body, requestConfig) => {
            const actionConfig = getActionConfig(url, 'POST', mergedConfig, requestConfig, body);
            return performHttpAction(actionConfig);
        },
        delete: async (url, requestConfig, body) => {
            const actionConfig = getActionConfig(url, 'DELETE', mergedConfig, requestConfig, body);
            return performHttpAction(actionConfig);
        },
        put: async (url, body, requestConfig) => {
            const actionConfig = getActionConfig(url, 'PUT', mergedConfig, requestConfig, body);
            return performHttpAction(actionConfig);
        },
        head: async (url, requestConfig) => {
            const actionConfig = getActionConfig(url, 'HEAD', mergedConfig, Object.assign({}, requestConfig));
            const response = await performHttpAction(Object.assign(Object.assign({}, actionConfig), { parseResponse: false }));
            return actionConfig.parseResponse ? response.status : response;
        }
    };
}
const getActionConfig = (url, httpMethod, clientConfig, requestConfig, body) => {
    const { baseUrl: clientBaseUrl, headers: clientHeaders } = clientConfig, otherClientConfig = __rest(clientConfig, ["baseUrl", "headers"]);
    const _a = requestConfig || {}, { baseUrl: requestBaseUrl, queryParams, urlParams, headers: requestHeaders } = _a, otherRequestConfig = __rest(_a, ["baseUrl", "queryParams", "urlParams", "headers"]);
    const formattedUrl = formatUrl(url, requestBaseUrl || clientBaseUrl, urlParams, queryParams);
    const headers = new Headers(clientHeaders);
    if (requestHeaders) {
        requestHeaders.forEach((value, name) => headers.set(name, value));
    }
    const hasContentTypeHeader = headers.has('content-type');
    if (body instanceof URLSearchParams) {
        if (!hasContentTypeHeader) {
            headers.set('content-type', 'application/x-www-form-urlencoded');
        }
        body = body.toString();
    }
    else if (body instanceof FormData) {
        if (hasContentTypeHeader) {
            throw new Error('A FormData body must have an empty content-type header so that the browser can generate multipart/form-data boundaries.');
        }
    }
    else if (typeof body === 'object') {
        if (!hasContentTypeHeader) {
            headers.set('content-type', 'application/json');
        }
        body = JSON.stringify(body);
    }
    return Object.assign(Object.assign(Object.assign({}, otherClientConfig), otherRequestConfig), { headers,
        httpMethod, url: formattedUrl, body });
};
//# sourceMappingURL=create-http-client.js.map